import React from 'react';
import { Box, useTheme } from '@mui/material';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { exportFieldTooltips } from '../../../shared/config/actionHelpCatalog';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import { CaptionText } from '../../../shared/ui/typography';
import { getWorkflowWorkspacePalette } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { layoutGap } from '../../../theme/theme';

interface ExportScopeSummaryProps {
  workspaceTitle: string;
  systemCountRef?: React.Ref<HTMLElement>;
  systemsLoading: boolean;
  systemsStale: boolean;
  selectedCount: number;
  totalCount: number;
  batchNotValidatedCount: number;
  batchWarningCount: number;
  batchErrorCount: number;
  selectionSummaryRef?: React.RefObject<HTMLDivElement | null>;
}

export default function ExportScopeSummary({
  workspaceTitle,
  systemCountRef,
  systemsLoading,
  systemsStale,
  selectedCount,
  totalCount,
  batchNotValidatedCount,
  batchWarningCount,
  batchErrorCount,
  selectionSummaryRef,
}: ExportScopeSummaryProps) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const hasSelection = selectedCount > 0;
  const hasBatchAttention = batchNotValidatedCount > 0 || batchWarningCount > 0 || batchErrorCount > 0;
  const selectionLabel = hasSelection
    ? `${selectedCount} / ${totalCount} selected`
    : `${totalCount} system${totalCount === 1 ? '' : 's'}`;
  const batchColor =
    batchErrorCount > 0
      ? theme.palette.error.main
      : batchNotValidatedCount > 0
        ? theme.palette.warning.main
        : batchWarningCount > 0
          ? theme.palette.warning.main
          : theme.palette.success.main;
  let batchMessage = 'Ready';
  if (batchNotValidatedCount > 0) {
    batchMessage = `${batchNotValidatedCount} ${batchNotValidatedCount === 1 ? 'needs' : 'need'} validation`;
  } else if (batchWarningCount > 0 || batchErrorCount > 0) {
    const parts: string[] = [];
    if (batchErrorCount > 0) {
      parts.push(`${batchErrorCount} error${batchErrorCount !== 1 ? 's' : ''}`);
    }
    if (batchWarningCount > 0) {
      parts.push(`${batchWarningCount} warning${batchWarningCount !== 1 ? 's' : ''}`);
    }
    batchMessage = parts.join(' · ');
  }

  return (
    <Box
      ref={selectionSummaryRef}
      role="group"
      aria-label={`${workspaceTitle}: export scope and validation status`}
      data-testid="export-pcf-tour-target-selection-summary"
      sx={{
        gridArea: 'scope',
        display: 'flex',
        alignItems: 'center',
        gap: layoutGap.dense,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <ActionHelpTooltip help={exportFieldTooltips.selectionSummary} wrapperSx={{ minWidth: 0 }}>
        {systemsLoading ? (
          <OperationProgressIndicator size={14} ariaLabel="Loading piping systems" />
        ) : (
          <Box
            ref={systemCountRef}
            component="span"
            sx={{
              display: 'inline-flex',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <CaptionText
              component="span"
              sx={{
                color: workspacePalette.tertiaryText,
                fontWeight: 600,
                lineHeight: 1.2,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectionLabel}{systemsStale ? ' · refresh' : ''}
            </CaptionText>
          </Box>
        )}
      </ActionHelpTooltip>
      <ActionHelpTooltip help={exportFieldTooltips.batchStatusSummary} wrapperSx={{ minWidth: 0 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Box
            component="span"
            aria-hidden="true"
            sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: batchColor, flexShrink: 0 }}
          />
          <CaptionText
            sx={{
              color: batchColor,
              fontWeight: hasBatchAttention ? 600 : 500,
              lineHeight: 1.2,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {batchMessage}
          </CaptionText>
        </Box>
      </ActionHelpTooltip>
    </Box>
  );
}
